import { useState, useEffect, useCallback, useRef } from 'react';
import { cartService } from '../services/cartService';

/**
 * Función auxiliar para obtener la mejor imagen del producto desde el backend.
 * Prioriza: current_image > images[0].src > product_card_image > image
 * Si la URL es relativa, no le agrega prefijo porque el backend ya devuelve URLs completas.
 */
export const getBestProductImage = (product) => {
  // Prioridad 1: current_image (campo nuevo del backend que ya tiene la lógica correcta)
  if (product.current_image) {
    return product.current_image;
  }

  // Prioridad 2: Primera imagen del array de imágenes (fuente más actualizada)
  if (product.images && product.images.length > 0 && product.images[0]?.src) {
    return product.images[0].src;
  }

  // Prioridad 3: product_card_image (puede estar desactualizada)
  if (product.product_card_image) {
    return product.product_card_image;
  }

  // Prioridad 4: campo image genérico
  if (product.image) {
    return product.image;
  }

  return '';
};

/**
 * Hook personalizado para manejar el carrito persistente desde el backend
 */
export const useCart = () => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Numero de la ultima carga pedida. Al cambiar de direccion en el checkout se
  // recarga el carrito con otra comuna (task-1379); si una respuesta vieja llega
  // despues que la nueva, pintaria el descuento de la direccion anterior.
  const ultimaCargaRef = useRef(0);

  // Verificar autenticación
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('auth_token');
      const user = localStorage.getItem('user');
      setIsAuthenticated(!!token && !!user);
    }
  }, []);

  // Cargar carrito al montar el componente si está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      loadCart();
    } else {
      setLoading(false);
      setCart(null);
    }
  }, [isAuthenticated]);

  /**
   * Cargar el carrito desde la API
   */
  const loadCart = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    const estaCarga = ++ultimaCargaRef.current;
    const result = await cartService.getCart();

    // Hubo otra carga despues de esta: manda la mas nueva.
    if (estaCarga !== ultimaCargaRef.current) return;
    
    if (result.success) {
      setCart(result.data);
      // Sincronizar con localStorage para compatibilidad con código existente
      if (result.data && result.data.items) {
        const productsCart = result.data.items.map(item => ({
          product_id: item.product.internal_id || item.product.id,
          wordpress_id: item.product.wordpress_id || item.product.id,
          sku: item.product.sku,
          name: item.product.name,
          slug: item.product.slug,
          price: item.product.price,
          sale_price: item.product.sale_price,
          image: getBestProductImage(item.product),
          description: item.product.description,
          quantity: item.quantity,
          tags: item.product.tags,
          stock_quantity: item.product.stock_quantity,
          stock_status: item.product.stock_status || "instock",
          total_sales: item.product.total_sales,
          box: item.product.box,
          categories: item.product.categories,
          analytics: item.product.analytics || null,
          // Guardar el item_id del backend para futuras operaciones
          cart_item_id: item.id,
          // Guardar información de cambio de precio
          price_changed: item.price_changed || false,
          price_change_info: item.price_change_info || null,
        }));
        localStorage.setItem('products_cart', JSON.stringify(productsCart));
      } else {
        localStorage.setItem('products_cart', JSON.stringify([]));
      }
    } else {
      setError(result.error);
      setCart(null);
    }

    setLoading(false);
  }, [isAuthenticated]);

  /**
   * Agregar producto al carrito
   */
  const addToCart = useCallback(async (productId, quantity = 1) => {
    if (!isAuthenticated) {
      return {
        success: false,
        error: 'Debes iniciar sesión para agregar productos al carrito',
      };
    }

    setError(null);
    const result = await cartService.addItem(productId, quantity);

    if (result.success) {
      setCart(result.data);
      // Sincronizar con localStorage
      if (result.data && result.data.items) {
        const productsCart = result.data.items.map(item => ({
          product_id: item.product.internal_id || item.product.id,
          wordpress_id: item.product.wordpress_id || item.product.id,
          sku: item.product.sku,
          name: item.product.name,
          slug: item.product.slug,
          price: item.product.price,
          sale_price: item.product.sale_price,
          image: getBestProductImage(item.product),
          description: item.product.description,
          quantity: item.quantity,
          tags: item.product.tags,
          stock_quantity: item.product.stock_quantity,
          total_sales: item.product.total_sales,
          box: item.product.box,
          categories: item.product.categories,
          analytics: item.product.analytics || null,
          cart_item_id: item.id,
          // Guardar información de cambio de precio
          price_changed: item.price_changed || false,
          price_change_info: item.price_change_info || null,
        }));
        localStorage.setItem('products_cart', JSON.stringify(productsCart));
      }
      return { success: true, message: result.message };
    } else {
      setError(result.error);
      return { success: false, error: result.error, errors: result.errors };
    }
  }, [isAuthenticated]);

  /**
   * Actualizar cantidad de un item
   */
  const updateQuantity = useCallback(async (itemId, quantity) => {
    if (!isAuthenticated) return { success: false, error: 'No autenticado' };

    setError(null);
    const result = await cartService.updateItem(itemId, quantity);

    if (result.success) {
      setCart(result.data);
      // Sincronizar con localStorage
      if (result.data && result.data.items) {
        const productsCart = result.data.items.map(item => ({
          product_id: item.product.internal_id || item.product.id,
          wordpress_id: item.product.wordpress_id || item.product.id,
          sku: item.product.sku,
          name: item.product.name,
          slug: item.product.slug,
          price: item.product.price,
          sale_price: item.product.sale_price,
          image: getBestProductImage(item.product),
          description: item.product.description,
          quantity: item.quantity,
          tags: item.product.tags,
          stock_quantity: item.product.stock_quantity,
          total_sales: item.product.total_sales,
          box: item.product.box,
          categories: item.product.categories,
          analytics: item.product.analytics || null,
          cart_item_id: item.id,
          // Guardar información de cambio de precio
          price_changed: item.price_changed || false,
          price_change_info: item.price_change_info || null,
        }));
        localStorage.setItem('products_cart', JSON.stringify(productsCart));
      }
      return { success: true, message: result.message };
    } else {
      setError(result.error);
      return { success: false, error: result.error };
    }
  }, [isAuthenticated]);

  /**
   * Eliminar item del carrito
   */
  const removeFromCart = useCallback(async (itemId) => {
    if (!isAuthenticated) return { success: false, error: 'No autenticado' };

    setError(null);
    const result = await cartService.removeItem(itemId);

    if (result.success) {
      setCart(result.data);
      // Sincronizar con localStorage
      if (result.data && result.data.items) {
        const productsCart = result.data.items.map(item => ({
          product_id: item.product.internal_id || item.product.id,
          wordpress_id: item.product.wordpress_id || item.product.id,
          sku: item.product.sku,
          name: item.product.name,
          slug: item.product.slug,
          price: item.product.price,
          sale_price: item.product.sale_price,
          image: getBestProductImage(item.product),
          description: item.product.description,
          quantity: item.quantity,
          tags: item.product.tags,
          stock_quantity: item.product.stock_quantity,
          total_sales: item.product.total_sales,
          box: item.product.box,
          categories: item.product.categories,
          analytics: item.product.analytics || null,
          cart_item_id: item.id,
          // Guardar información de cambio de precio
          price_changed: item.price_changed || false,
          price_change_info: item.price_change_info || null,
        }));
        localStorage.setItem('products_cart', JSON.stringify(productsCart));
      } else {
        localStorage.setItem('products_cart', JSON.stringify([]));
      }
      return { success: true, message: result.message };
    } else {
      setError(result.error);
      return { success: false, error: result.error };
    }
  }, [isAuthenticated]);

  /**
   * Limpiar todo el carrito
   */
  const clearCart = useCallback(async () => {
    if (!isAuthenticated) return { success: false, error: 'No autenticado' };

    setError(null);
    const result = await cartService.clearCart();

    if (result.success) {
      setCart(result.data);
      localStorage.setItem('products_cart', JSON.stringify([]));
      return { success: true, message: result.message };
    } else {
      setError(result.error);
      return { success: false, error: result.error };
    }
  }, [isAuthenticated]);

  // Calcular totales
  const totals = cart ? {
    total: parseFloat(cart.total || 0),
    subtotal: parseFloat(cart.subtotal || 0),
    totalQuantity: cart.total_quantity || 0,
    itemsCount: cart.items_count || 0,
  } : {
    total: 0,
    subtotal: 0,
    totalQuantity: 0,
    itemsCount: 0,
  };

  return {
    cart,
    loading,
    error,
    isAuthenticated,
    totals,
    loadCart,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
  };
};